import { useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  translatedStatus,
  translatedSeason,
  daysTranslation,
} from '../../data/translationMap'
import { openNotRegisteredPopup } from '../../store/slices/ui.slice'
import { WatchingOnline } from '../../assets/imgs/svg/AnimeDetails/desktop/WatchingOnline'
import { AddToWatch } from '../../assets/imgs/svg/AnimeDetails/desktop/AddToWatch'
import { PlanToWatch } from '../../assets/imgs/svg/AnimeDetails/desktop/PlanToWatch'
import { Year } from '../../assets/imgs/svg/AnimeDetails/desktop/Year'
import { ReviewPopup } from '../ReviewSystem/ReviewPopup'
import { AddReview } from '../../assets/imgs/svg/AnimeDetails/desktop/AddReview'
import { EditReview } from '../../assets/imgs/svg/AnimeDetails/desktop/EditReview'
import { EditWatchlist } from '../../assets/imgs/svg/AnimeDetails/desktop/EditWatchlist'
import toast from 'react-hot-toast'
import { AnimeWatchlistEdit } from '../UserProfile/UserWatchlist/AnimeWatchlistEdit'
import { useWatchlistActions } from '../../hooks/useWatchlistActions'
import { getAnimeImage } from '../../services/util.service'
import { MinusIcon, PlusIcon } from '../ui/icons/shared'

export function AnimeHeader({
  anime,
  handleAddItem,
  loggedInUser,
  dispatch,
  isInWatchlist,
  currentWatchItem,
  onIncrement,
  onDecrement,
  onRemove,
  onSaveChanges,
  canEdit,
}) {
  const [showReviewPopup, setShowReviewPopup] = useState(false)
  const [initialReviewData, setInitialReviewData] = useState(null)
  const [showEditWatchlist, setShowEditWatchlist] = useState(false)
  const [editData, setEditData] = useState(null)

  const reviews = useSelector((state) => state.reviewModule.reviews) || []

  const { getUpdatedWatchItem } = useWatchlistActions(loggedInUser?._id)

  const filteredReviews = reviews.filter(
    (review) => review.animeId === anime.anizone_id
  )

  const userAlreadyReviewed = useMemo(() => {
    if (!loggedInUser) return false
    return filteredReviews.some((review) => review.userId === loggedInUser._id)
  }, [filteredReviews, loggedInUser])

  const statuses = [
    { value: 'Watching', label: 'כרגע צופה' },
    { value: 'Plan to Watch', label: 'מתכנן לצפות' },
    { value: 'Completed', label: 'סיימתי לצפות' },
    { value: 'On-Hold', label: 'בהמתנה' },
    { value: 'Dropped', label: 'ננטש' },
  ]

  const currentStatusLabel = currentWatchItem
    ? statuses.find((status) => status.value === currentWatchItem.status)?.label
    : null

  const handleWriteReview = () => {
    if (!loggedInUser) {
      toast.error('עליך להיות מחובר כדי לכתוב ביקורת.')
      dispatch(openNotRegisteredPopup())
      return
    }

    const existingReview = filteredReviews.find(
      (review) =>
        review.animeId === anime.anizone_id &&
        review.userId === loggedInUser._id
    )
    setInitialReviewData(existingReview || null)
    setShowReviewPopup(true)
  }

  const handleEditWatchlist = () => {
    if (!currentWatchItem) return
    setEditData({
      progress: currentWatchItem.progress,
      status: currentWatchItem.status,
    })
    setShowEditWatchlist(true)
  }

  return (
    <div className="w-[226px]">
      <div className="max-h-full sticky top-[100px]">
        <div className="w-full h-[350px] relative">
          <div className="absolute -translate-y-[40%] right-[-10%]">
            <Year anime={anime} />
          </div>
          <img
            src={getAnimeImage(anime)}
            className="w-full h-full object-cover"
            alt={`${anime.title_english || anime.title} Cover Image`}
          />
        </div>

        <div className="flex text-center justify-between my-6">
          <div>
            <p className="text-muted text-sm">עונה</p>
            <span className="text-softText text-lg">
              {translatedSeason[anime.season] || anime.season || 'לא צויין'}
            </span>
          </div>
          <div>
            <p className="text-muted text-sm">זמני שידור</p>
            <span className="text-softText text-lg">
              {daysTranslation[anime.broadcast?.day] ||
                anime.broadcast?.day ||
                'לא צויין'}
              {anime.broadcast?.time ? `, ${anime.broadcast.time}` : ''}
            </span>
          </div>
          <div>
            <p className="text-muted text-sm">סטטוס</p>
            <span className="text-softText text-lg">
              {translatedStatus[anime.status] || anime.status || 'לא צויין'}
            </span>
          </div>
        </div>

        <div className="flex flex-col justify-center items-center gap-4">
          {!isInWatchlist && (
            <>
              <button
                className="hover:scale-105 transform duration-300"
                onClick={() => handleAddItem('Watching', anime)}
              >
                <PlanToWatch />
              </button>
              <button
                className="hover:scale-105 transform duration-300"
                onClick={() => handleAddItem('Plan to Watch', anime)}
              >
                <AddToWatch />
              </button>
            </>
          )}

          {isInWatchlist && (
            <>
              <span className="w-full h-[2px] bg-[rgba(255,255,255,0.15)]"></span>
              {currentStatusLabel && (
                <div className="flex flex-col items-center gap-2 justify-center">
                  <span className="text-softText text-lg">
                    {currentStatusLabel}
                  </span>
                </div>
              )}

              {currentWatchItem && (
                <div className="flex gap-2 items-center">
                  {canEdit && onDecrement && onIncrement ? (
                    <>
                      <button
                        onClick={() => onDecrement(currentWatchItem)}
                        className="w-6 h-6 bg-accentLight rounded-full flex items-center justify-center"
                      >
                        <MinusIcon />
                      </button>
                      <span className="text-softText">
                        {anime.episodes || '??'} / {currentWatchItem.progress}
                      </span>
                      <button
                        onClick={() => onIncrement(currentWatchItem)}
                        className="w-6 h-6 bg-accentLight rounded-full flex items-center justify-center"
                      >
                        <PlusIcon />
                      </button>
                    </>
                  ) : (
                    <span className="text-softText">
                      {currentWatchItem.progress} / {anime.episodes || '??'}
                    </span>
                  )}
                </div>
              )}

              <button onClick={handleEditWatchlist}>
                <EditWatchlist />
              </button>
              <span className="w-full h-[2px] bg-[rgba(255,255,255,0.15)]"></span>

              <button onClick={handleWriteReview}>
                {userAlreadyReviewed ? <EditReview /> : <AddReview />}
              </button>
            </>
          )}

          {anime.streaming.find(
            (s) => s.name === 'Crunchyroll' || s.name === 'Netflix'
          ) && (
            <div className="flex flex-col hover:scale-105 transform duration-300 items-center">
              <a
                href={
                  anime.streaming.find((s) => s.name === 'Crunchyroll')?.url ||
                  anime.streaming.find((s) => s.name === 'Netflix')?.url ||
                  '#'
                }
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center"
                onClick={(e) => {
                  if (!loggedInUser) {
                    e.preventDefault()
                    dispatch(openNotRegisteredPopup())
                  }
                }}
              >
                <WatchingOnline />
              </a>
            </div>
          )}
        </div>
      </div>

      {showReviewPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <ReviewPopup
            initialData={initialReviewData}
            animeId={anime.anizone_id}
            loggedInUser={loggedInUser}
            onClose={() => setShowReviewPopup(false)}
          />
        </div>
      )}

      {showEditWatchlist && currentWatchItem && editData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <AnimeWatchlistEdit
            item={currentWatchItem}
            editData={editData}
            setEditData={setEditData}
            handleSaveItemChanges={(updatedItem) => {
              onSaveChanges(updatedItem)
              setShowEditWatchlist(false)
            }}
            getUpdatedWatchItem={getUpdatedWatchItem}
            handleCloseEdit={() => setShowEditWatchlist(false)}
            handleOpenReviewPopup={() => {}}
            userAlreadyReviewed={() => false}
            onRemove={(anizoneId) => {
              onRemove(anizoneId)
              setShowEditWatchlist(false)
            }}
          />
        </div>
      )}
    </div>
  )
}
